import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAssistantDismiss } from './host';
import type {
  AssistantAction,
  AssistantActionResult,
  CreateTaskAction,
  PostMessageAction,
  SetMemberAccessAction,
  SetWorkspaceFeatureAction,
  UpdateTaskAction,
} from '@itsappy/shared';
import { TASK_PRIORITIES, TASK_STATUSES } from '@itsappy/shared';
import { ApiError } from '@/lib/api';
import {
  useChannels,
  useCreateTask,
  useMemberUsers,
  useProjects,
  useSendMessage,
  useSetMemberAccess,
  useSetWorkspaceFeature,
  useUpdateTask,
  useWorkspace,
} from '@/hooks/queries';
import { useRecordAssistantAction } from '@/hooks/assistant';
import { FEATURE_DETAILS } from '@/components/admin/featureDetails';
import { Avatar, Spinner } from '@/components/ui';
import {
  CheckIcon,
  ChannelIcon,
  PeopleIcon,
  SparkleIcon,
  TasksIcon,
} from '@/components/ui/icons';
import { toast } from '@/store/ui';
import { cn } from '@/lib/utils';

/**
 * A proposal, as something you can read and change before it happens.
 *
 * This is the whole point of the redesign. Asked to add a task for someone,
 * the old assistant wrote a paragraph back asking which project — a question
 * that costs a sentence to ask, a sentence to answer and another round trip to
 * resolve. Here the same question is a dropdown that already has the likely
 * answer selected, and the task is one click away.
 *
 * Nothing is created until that click, and the click goes through the ordinary
 * API: same permissions, same notifications, same realtime as a task made by
 * hand. The assistant proposes; the person still acts.
 */
export function ActionCard({
  action,
  messageId,
  result,
}: {
  action: AssistantAction;
  messageId: string;
  result?: AssistantActionResult;
}) {
  if (result) return <Settled result={result} />;
  switch (action.kind) {
    case 'create_task':
      return <CreateTaskCard action={action} messageId={messageId} />;
    case 'update_task':
      return <UpdateTaskCard action={action} messageId={messageId} />;
    case 'set_workspace_feature':
      return <WorkspaceFeatureCard action={action} messageId={messageId} />;
    case 'set_member_access':
      return <MemberAccessCard action={action} messageId={messageId} />;
    default:
      return <PostMessageCard action={action} messageId={messageId} />;
  }
}

/** What a card becomes once it has been run, or waved away. */
function Settled({ result }: { result: AssistantActionResult }) {
  const dismiss = useAssistantDismiss();

  if (result.state === 'dismissed') {
    return <p className="mt-2 text-xs text-slate-500">Dismissed.</p>;
  }
  return (
    <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-400">
      <CheckIcon size="xs" />
      {result.link ? (
        // Closing on the way out: the destination would otherwise open behind
        // the assistant. See `AssistantHost`.
        <Link
          to={result.link}
          onClick={dismiss}
          className="underline decoration-emerald-400/40 hover:text-emerald-300"
        >
          {result.label ?? 'Done'}
        </Link>
      ) : (
        (result.label ?? 'Done')
      )}
    </p>
  );
}

/** The card's own chrome, shared so both actions read as the same object. */
function Shell({
  icon,
  kind,
  children,
}: {
  icon: React.ReactNode;
  kind: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-2 animate-slide-up overflow-hidden rounded-xl border border-brand-500/30 bg-brand-500/[0.06]">
      <div className="flex items-center gap-1.5 border-b border-brand-500/20 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-300">
        {icon}
        {kind}
      </div>
      <div className="space-y-2.5 p-3">{children}</div>
    </div>
  );
}

/** One labelled control on a card, laid out so the label never costs a line. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="w-16 shrink-0 text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const compact =
  'min-w-0 flex-1 rounded-md border border-surface-4 bg-surface-2 px-2 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.message : 'That did not go through';
}

function CreateTaskCard({
  action,
  messageId,
}: {
  action: CreateTaskAction;
  messageId: string;
}) {
  const { data: projects } = useProjects();
  const members = useMemberUsers();
  const create = useCreateTask();
  const record = useRecordAssistantAction();

  const [title, setTitle] = useState(action.title);
  const [projectId, setProjectId] = useState(action.projectId ?? '');
  const [assigneeIds, setAssigneeIds] = useState(action.assigneeIds);
  const [priority, setPriority] = useState(action.priority);
  // The API wants an instant; a date field speaks days. The clock is fixed at
  // midday so a timezone west of the workspace cannot walk the date backwards.
  const [due, setDue] = useState(action.dueDate?.slice(0, 10) ?? '');

  const options = projects?.items.filter((project) => !project.archived) ?? [];
  const project = options.find((option) => option.id === projectId);
  const assignee = members.find((member) => member.id === assigneeIds[0]);

  function run() {
    if (!projectId) return;
    create.mutate(
      {
        projectId,
        title: title.trim(),
        description: action.description,
        status: 'todo',
        priority,
        assigneeIds,
        dueDate: due ? new Date(`${due}T12:00:00`).toISOString() : undefined,
      },
      {
        onSuccess: (task) => {
          record.mutate({
            messageId,
            state: 'done',
            label: project ? `${project.key}-${task.number}` : task.title,
            link: `/tasks/${task.id}`,
          });
          toast.success(project ? `Created ${project.key}-${task.number}` : 'Task created');
        },
        onError: (error) => toast.error('Could not create the task', errorText(error)),
      },
    );
  }

  return (
    <Shell icon={<TasksIcon size="xs" />} kind="New task">
      <input
        className={cn(compact, 'w-full text-sm font-medium')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        aria-label="Task title"
      />

      {action.description && <p className="text-xs text-slate-400">{action.description}</p>}

      <Field label="Project">
        <select
          className={compact}
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-invalid={!projectId}
        >
          <option value="">Choose a project…</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Assignee">
        {/* Single-select on purpose: the request that reaches an assistant is
         * "add a task for X", and a multi-select would spend a row of chrome
         * on a case that barely comes up. The task detail takes the rest. */}
        <select
          className={compact}
          value={assigneeIds[0] ?? ''}
          onChange={(e) => setAssigneeIds(e.target.value ? [e.target.value] : [])}
        >
          <option value="">Nobody yet</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.name}
            </option>
          ))}
        </select>
      </Field>

      <div className="flex gap-2">
        <Field label="Priority">
          <select
            className={compact}
            value={priority}
            onChange={(e) => setPriority(e.target.value as CreateTaskAction['priority'])}
          >
            {TASK_PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {value === 'none' ? 'No priority' : value[0].toUpperCase() + value.slice(1)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Due">
          <input
            type="date"
            className={compact}
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </Field>
      </div>

      <Footer
        run={run}
        runLabel="Create task"
        busy={create.isPending}
        disabled={!projectId || !title.trim()}
        blocked={!projectId ? 'Pick a project first' : undefined}
        messageId={messageId}
      >
        {assignee && (
          <span className="flex items-center gap-1.5 text-[11px] text-slate-400">
            <Avatar user={assignee} size="xs" />
            {assignee.name} will be assigned
          </span>
        )}
      </Footer>
    </Shell>
  );
}

function PostMessageCard({
  action,
  messageId,
}: {
  action: PostMessageAction;
  messageId: string;
}) {
  const { data: channels } = useChannels();
  const record = useRecordAssistantAction();
  const [channelId, setChannelId] = useState(action.channelId ?? '');
  const [body, setBody] = useState(action.body);
  const send = useSendMessage(channelId);

  const options = channels?.items.filter((channel) => channel.kind === 'channel') ?? [];
  const channel = options.find((option) => option.id === channelId);

  function run() {
    if (!channelId || !body.trim()) return;
    send.mutate(
      { body: body.trim() },
      {
        onSuccess: () => {
          record.mutate({
            messageId,
            state: 'done',
            label: channel ? `Posted to #${channel.name}` : 'Posted',
            link: `/chat/${channelId}`,
          });
          toast.success(channel ? `Posted to #${channel.name}` : 'Message posted');
        },
        onError: (error) => toast.error('Could not post', errorText(error)),
      },
    );
  }

  return (
    <Shell icon={<ChannelIcon size="xs" />} kind="Post a message">
      <textarea
        rows={3}
        className={cn(compact, 'w-full resize-y text-sm')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        aria-label="Message"
      />

      <Field label="Channel">
        <select
          className={compact}
          value={channelId}
          onChange={(e) => setChannelId(e.target.value)}
          aria-invalid={!channelId}
        >
          <option value="">Choose a channel…</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              #{option.name}
            </option>
          ))}
        </select>
      </Field>

      <Footer
        run={run}
        runLabel={channel ? `Post to #${channel.name}` : 'Post'}
        busy={send.isPending}
        disabled={!channelId || !body.trim()}
        blocked={!channelId ? 'Pick a channel first' : undefined}
        messageId={messageId}
      >
        <span className="text-[11px] text-slate-500">Everyone in the channel will see it.</span>
      </Footer>
    </Shell>
  );
}

/**
 * Work on a task that already exists.
 *
 * Only the fields the request spoke to are on the card. That is not a saving
 * of chrome — a card carrying the task's whole current state would send it all
 * back on confirm, and "mark it done" would silently undo whatever somebody
 * changed in the minutes between the question and the click.
 */
function UpdateTaskCard({
  action,
  messageId,
}: {
  action: UpdateTaskAction;
  messageId: string;
}) {
  const members = useMemberUsers();
  const update = useUpdateTask();
  const record = useRecordAssistantAction();

  const [status, setStatus] = useState(action.status);
  const [priority, setPriority] = useState(action.priority);
  const [assigneeIds, setAssigneeIds] = useState(action.assigneeIds);
  // Three states, not two: absent leaves the deadline alone, an empty string
  // clears it, a date sets it. `null` from the model means the second.
  const [due, setDue] = useState(
    action.dueDate === undefined ? undefined : (action.dueDate?.slice(0, 10) ?? ''),
  );

  function run() {
    update.mutate(
      {
        id: action.taskId,
        ...(status !== undefined && { status }),
        ...(priority !== undefined && { priority }),
        ...(assigneeIds !== undefined && { assigneeIds }),
        // An empty string is how this API clears a date; `null` is a
        // validation error, and leaving the field out means "don't touch it".
        ...(due !== undefined && {
          dueDate: due ? new Date(`${due}T12:00:00`).toISOString() : '',
        }),
      },
      {
        onSuccess: () => {
          record.mutate({
            messageId,
            state: 'done',
            label: `Updated ${action.label}`,
            link: `/tasks/${action.taskId}`,
          });
          toast.success(`Updated ${action.label}`);
        },
        // `useUpdateTask` already reports the failure — a second toast saying
        // the same thing twice is how one refused edit looks like two.
        onError: () => {},
      },
    );
  }

  return (
    <Shell icon={<TasksIcon size="xs" />} kind="Update task">
      <p className="text-sm font-medium text-ink">{action.label}</p>

      {status !== undefined && (
        <Field label="Status">
          <select
            className={compact}
            value={status}
            onChange={(e) => setStatus(e.target.value as UpdateTaskAction['status'])}
          >
            {TASK_STATUSES.map((value) => (
              <option key={value} value={value}>
                {value.replace('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      )}

      {priority !== undefined && (
        <Field label="Priority">
          <select
            className={compact}
            value={priority}
            onChange={(e) => setPriority(e.target.value as UpdateTaskAction['priority'])}
          >
            {TASK_PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {value === 'none' ? 'No priority' : value.replace('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      )}

      {assigneeIds !== undefined && (
        <Field label="Assignee">
          <select
            className={compact}
            value={assigneeIds[0] ?? ''}
            onChange={(e) => setAssigneeIds(e.target.value ? [e.target.value] : [])}
          >
            <option value="">Nobody</option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      {due !== undefined && (
        <Field label="Due">
          <input
            type="date"
            className={compact}
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </Field>
      )}

      <Footer
        run={run}
        runLabel="Save change"
        busy={update.isPending}
        disabled={false}
        messageId={messageId}
      >
        <span className="text-[11px] text-slate-500">Nothing else on the task is touched.</span>
      </Footer>
    </Shell>
  );
}

/**
 * A product switched on or off for the whole workspace.
 *
 * The card is the confirmation. Administration asks twice before switching a
 * product off — a dialog on top of a button — and that is right for a control
 * somebody might brush past on a settings screen. Here the proposal was
 * already read as a sentence before it was drawn, so the second ask would be
 * the same question a third time; what it owes the person instead is a plain
 * statement of who this lands on, which is everyone.
 */
function WorkspaceFeatureCard({
  action,
  messageId,
}: {
  action: SetWorkspaceFeatureAction;
  messageId: string;
}) {
  const { data: workspace } = useWorkspace();
  const setFeature = useSetWorkspaceFeature();
  const record = useRecordAssistantAction();
  const detail = FEATURE_DETAILS[action.feature];

  function run() {
    if (!workspace) return;
    setFeature.mutate(
      {
        feature: action.feature,
        enabled: action.enabled,
        disabledFeatures: workspace.settings.disabledFeatures,
      },
      {
        onSuccess: () => {
          record.mutate({
            messageId,
            state: 'done',
            label: action.enabled
              ? `${detail.label} is back on`
              : `${detail.label} switched off`,
            link: '/admin',
          });
          toast.success(
            action.enabled ? `${detail.label} is back on` : `${detail.label} switched off`,
          );
        },
        onError: (error) => toast.error('Could not save that', errorText(error)),
      },
    );
  }

  return (
    <Shell icon={<SparkleIcon size="xs" />} kind="Workspace setting">
      <p className="flex items-center gap-2 text-sm font-medium text-ink">
        <detail.Icon size="sm" className="text-brand-300" />
        {action.enabled
          ? `Turn ${detail.label} on for everyone`
          : `Turn ${detail.label} off for everyone`}
      </p>
      <p className="text-xs text-slate-400">
        {action.enabled
          ? `${detail.description}. Everyone gets it back exactly as they had it — personal access was remembered while it was off.`
          : `Nobody in this workspace will be able to open ${detail.label}, including you. What each person was granted is remembered and comes back when you switch it on again.`}
      </p>

      <Footer
        run={run}
        runLabel={action.enabled ? `Turn ${detail.label} on` : `Turn ${detail.label} off`}
        busy={setFeature.isPending}
        disabled={!workspace}
        destructive={!action.enabled}
        messageId={messageId}
      >
        <span className="text-[11px] text-slate-500">Applies to the whole workspace.</span>
      </Footer>
    </Shell>
  );
}

/**
 * One product given to, or taken from, named people.
 *
 * The names are spelled out rather than counted. "3 people" is a number
 * somebody has to trust; three faces are something they can check, and this is
 * the card where getting the wrong person is quietest to miss.
 */
function MemberAccessCard({
  action,
  messageId,
}: {
  action: SetMemberAccessAction;
  messageId: string;
}) {
  const members = useMemberUsers();
  const setAccess = useSetMemberAccess();
  const record = useRecordAssistantAction();
  const detail = FEATURE_DETAILS[action.feature];

  const people = action.userIds.flatMap((id) => {
    const user = members.find((member) => member.id === id);
    return user ? [user] : [];
  });
  const names = people.length
    ? people.map((person) => person.name).join(', ')
    : `${action.userIds.length} people`;

  function run() {
    setAccess.mutate(
      { userIds: action.userIds, feature: action.feature, enabled: action.enabled },
      {
        onSuccess: (result) => {
          const label = `${detail.label} ${action.enabled ? 'granted to' : 'removed from'} ${result.updated} ${
            result.updated === 1 ? 'person' : 'people'
          }`;
          record.mutate({ messageId, state: 'done', label, link: '/admin' });
          toast.success(label);
        },
        onError: (error) => toast.error('Could not update access', errorText(error)),
      },
    );
  }

  return (
    <Shell
      icon={<PeopleIcon size="xs" />}
      kind={action.enabled ? 'Give access' : 'Remove access'}
    >
      <p className="flex items-center gap-2 text-sm font-medium text-ink">
        <detail.Icon size="sm" className="text-brand-300" />
        {action.enabled ? `Give ${detail.label} to` : `Take ${detail.label} from`} {names}
      </p>

      {people.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {people.map((person) => (
            <li
              key={person.id}
              className="flex items-center gap-1.5 rounded-full bg-surface-2 py-0.5 pl-0.5 pr-2 text-xs text-slate-300"
            >
              <Avatar user={person} size="xs" />
              {person.name}
            </li>
          ))}
        </ul>
      )}

      <Footer
        run={run}
        runLabel={action.enabled ? `Give ${detail.label}` : `Remove ${detail.label}`}
        busy={setAccess.isPending}
        disabled={action.userIds.length === 0}
        destructive={!action.enabled}
        messageId={messageId}
      >
        <span className="text-[11px] text-slate-500">Nothing else they can open changes.</span>
      </Footer>
    </Shell>
  );
}

/**
 * The row that runs it.
 *
 * "Dismiss" is a real control rather than a way to hide the card: a proposal
 * nobody wanted should stop offering itself after a reload, same as one that
 * was taken.
 */
function Footer({
  run,
  runLabel,
  busy,
  disabled,
  blocked,
  destructive,
  messageId,
  children,
}: {
  run: () => void;
  runLabel: string;
  busy: boolean;
  disabled: boolean;
  blocked?: string;
  /** Takes something away rather than adding it — the button should say so. */
  destructive?: boolean;
  messageId: string;
  children?: React.ReactNode;
}) {
  const record = useRecordAssistantAction();

  return (
    <div className="-mx-3 -mb-3 mt-3 flex flex-wrap items-center gap-2 border-t border-brand-500/20 px-3 py-2">
      <button
        type="button"
        onClick={run}
        disabled={disabled || busy}
        className={cn('btn btn-sm', destructive ? 'btn-danger' : 'btn-primary')}
      >
        {busy && <Spinner />}
        {runLabel}
      </button>
      <button
        type="button"
        onClick={() => record.mutate({ messageId, state: 'dismissed' })}
        disabled={record.isPending}
        className="btn btn-ghost btn-sm"
      >
        Dismiss
      </button>
      <div className="ml-auto flex items-center gap-2">
        {blocked ? <span className="text-[11px] text-amber-400">{blocked}</span> : children}
      </div>
    </div>
  );
}
